import { z } from 'zod';
import {
  pagingFields,
  parseSearchParams,
  searchCount,
  type SearchFieldKinds,
  searchId,
  searchText,
  toSearchParams,
} from './search-spec.ts';

/**
 * End consumer search filter spec (Smart Search, MET-3528).
 *
 * Same contract shape as the other entity specs: the translator emits it,
 * `GET /end-consumers/filter` runs it, and it round-trips through the URL.
 * Consumers are `end_consumer_invite` rows grouped as in
 * `db/sql/list-consumers-by-customer.sql`. Account status (invited, active,
 * disabled) lives in Cognito, not the database, so it isn't a filter.
 *
 * Consumer names and emails are PII: they may appear in `text`, which is only
 * ever a bind parameter and is never logged.
 */

export const END_CONSUMER_SEARCH_SORT_COLUMNS = [
  'email',
  'name',
  'customerName',
  'meterCount',
  'lastMoveInDate',
] as const;

export const endConsumerSearchSchema = z
  .object({
    customerId: searchId.optional().describe('Only consumers of this customer'),
    text: searchText.optional().describe('Free-text match against consumer email or name'),
    meterCountMin: searchCount.optional().describe('At least this many meters'),
    meterCountMax: searchCount.optional().describe('At most this many meters'),
    // Ten years of move-ins: a product choice (like meter search's contact
    // windows), well inside what `CURRENT_DATE - $n::int` accepts.
    movedInWithinDays: z.number().int().positive().max(3_650).optional()
      .describe('Consumers with a meter move-in date within this many days'),
    ...pagingFields(END_CONSUMER_SEARCH_SORT_COLUMNS),
  })
  .strict()
  .refine(
    (s) =>
      s.meterCountMin === undefined || s.meterCountMax === undefined ||
      s.meterCountMin <= s.meterCountMax,
    { message: 'meterCountMin must not exceed meterCountMax', path: ['meterCountMin'] },
  );

export type EndConsumerSearch = z.infer<typeof endConsumerSearchSchema>;

/** One row of `GET /end-consumers/filter`. */
export interface EndConsumerSearchRow {
  email: string;
  name: string | null;
  customerId: number;
  customerName: string | null;
  sensorGroupId: number | null;
  meterCount: number;
  /** Latest meter move-in date, YYYY-MM-DD; null if none was recorded. */
  lastMoveInDate: string | null;
}

export interface EndConsumerSearchResponse {
  endConsumers: EndConsumerSearchRow[];
  total: number;
  page: number;
  pageSize: number;
}

const END_CONSUMER_SEARCH_FIELDS: SearchFieldKinds = {
  customerId: 'number',
  text: 'string',
  meterCountMin: 'number',
  meterCountMax: 'number',
  movedInWithinDays: 'number',
  page: 'number',
  pageSize: 'number',
  sortBy: 'string',
  sortDir: 'string',
};

/** Serialize an end consumer search to URL params (defaults omitted; see `toSearchParams`). */
export function toEndConsumerSearchParams(search: EndConsumerSearch): URLSearchParams {
  return toSearchParams(search, END_CONSUMER_SEARCH_FIELDS);
}

/** Parse URL params into a validated end consumer search (see `parseSearchParams`). */
export function parseEndConsumerSearchParams(
  params: URLSearchParams,
): ReturnType<typeof endConsumerSearchSchema.safeParse> {
  return parseSearchParams(endConsumerSearchSchema, END_CONSUMER_SEARCH_FIELDS, params);
}
